/**
 * Plain-language wording for the Website tab: periods, durations, page and device names. Owners read
 * "Phone", "Home page" and "2 min 10 sec", never an event name, a URL path on its own or a millisecond
 * count. Every phrase is a whole `t()` sentence so it translates naturally.
 */
import type { TFunction } from 'i18next';

import type { StatsDelta } from '../../mock-data/types';
import { dayRunWords } from '../home/stats-mapping';
import { deltaPct } from '../home/stats-shape';

import type { HostKind } from './api';
import { rangeDays, type Channel, type DeviceKind, type SystemKind, type WebsitePeriod } from './queries';

export const formatCount = (n: number, language: string): string => {
  try {
    return new Intl.NumberFormat(language || undefined).format(Math.round(n));
  } catch {
    return String(Math.round(n));
  }
};

/** "45 sec", "2 min", "2 min 10 sec". */
export const formatDuration = (seconds: number, t: TFunction): string => {
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return t('{{s}} sec', { s: total });
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s === 0 ? t('{{m}} min', { m }) : t('{{m}} min {{s}} sec', { m, s });
};

/** A custom range's days in words ("Sep 1", "Sep 30"), the year only when it is not this year. */
const rangeWords = (period: WebsitePeriod, language: string): { from: string; to: string; days: number } | null => {
  const range = rangeDays(period);
  if (!range) return null;
  return { ...dayRunWords(range.from, range.to - 1, language), days: range.days };
};

/** The period, as it ends a sentence ("… visited your website in the last 7 days."). */
export const periodPhrase = (interval: WebsitePeriod, t: TFunction, language = ''): string => {
  switch (interval) {
    case 'TODAY':
      return t('today');
    case 'YESTERDAY':
      return t('yesterday');
    case '7D':
      return t('in the last 7 days');
    case '30D':
      return t('in the last 30 days');
    case '90D':
      return t('in the last 90 days');
    default: {
      const words = rangeWords(interval, language);
      if (!words) return t('in the last 7 days');
      return words.days === 1
        ? t('on {{day}}', { day: words.from })
        : t('from {{from}} to {{to}}', { from: words.from, to: words.to });
    }
  }
};

/** The period before it, as it ends a sentence ("… more than the 7 days before."). */
export const previousPhrase = (interval: WebsitePeriod, t: TFunction): string => {
  switch (interval) {
    case 'TODAY':
      return t('yesterday at this time');
    case 'YESTERDAY':
      return t('the day before');
    case '7D':
      return t('the 7 days before');
    case '30D':
      return t('the 30 days before');
    case '90D':
      return t('the 90 days before');
    default: {
      const days = rangeDays(interval)?.days ?? 7;
      return days === 1 ? t('the day before') : t('the {{n}} days before', { n: days });
    }
  }
};

/** The caption under a figure's change ("vs the 7 days before"). */
export const comparisonCaption = (interval: WebsitePeriod, t: TFunction): string => {
  switch (interval) {
    case 'TODAY':
      return t('vs yesterday at this time');
    case 'YESTERDAY':
      return t('vs the day before');
    case '7D':
      return t('vs the 7 days before');
    case '30D':
      return t('vs the 30 days before');
    case '90D':
      return t('vs the 90 days before');
    default: {
      const days = rangeDays(interval)?.days ?? 7;
      return days === 1 ? t('vs the day before') : t('vs the {{n}} days before', { n: days });
    }
  }
};

export const intervalLabel = (interval: WebsitePeriod, t: TFunction, language = ''): string => {
  switch (interval) {
    case 'TODAY':
      return t('Today');
    case 'YESTERDAY':
      return t('Yesterday');
    case '7D':
      return t('Last 7 days');
    case '30D':
      return t('Last 30 days');
    case '90D':
      return t('Last 90 days');
    default: {
      const words = rangeWords(interval, language);
      if (!words) return t('Last 7 days');
      return words.days === 1 ? words.from : `${words.from} – ${words.to}`;
    }
  }
};

// A leading language segment ("/en/casino", "/pt-br/promotions") says nothing about the page itself.
const LOCALE_SEGMENT = /^[a-z]{2}(-[a-z]{2})?$/i;

const humanize = (segment: string): string => {
  let s = segment;
  try {
    s = decodeURIComponent(segment);
  } catch {
    // keep the raw segment
  }
  s = s.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
};

/** A page's friendly name: "Home page" for the root, else its last path segment in words ("Welcome bonus"). */
export const pageName = (path: string, t: TFunction): string => {
  const segments = path.split('/').filter(Boolean);
  const meaningful = segments.length > 0 && LOCALE_SEGMENT.test(segments[0]) ? segments.slice(1) : segments;
  if (meaningful.length === 0) return t('Home page');
  return humanize(meaningful[meaningful.length - 1]) || path;
};

export const deviceName = (kind: DeviceKind, t: TFunction): string => {
  switch (kind) {
    case 'mobile':
      return t('Phone');
    case 'desktop':
      return t('Computer');
    case 'tablet':
      return t('Tablet');
    case 'other':
      return t('Other');
  }
};

/** An operating system's own name (brand names, so not translated, except "Other"). */
export const systemName = (kind: SystemKind, t: TFunction): string => {
  switch (kind) {
    case 'ios':
      return 'iOS';
    case 'android':
      return 'Android';
    case 'windows':
      return 'Windows';
    case 'macos':
      return 'macOS';
    case 'other':
      return t('Other');
  }
};

/** The change against the period before, as the Stats page shows it: from an empty period it reads +100%. */
export const periodDelta = (current: number, previous: number): StatsDelta => {
  const pct = deltaPct(current, previous);
  return { label: `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`, dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
};

export const viewsText = (n: number, t: TFunction, language: string): string =>
  n === 1 ? t('1 view') : t('{{n}} views', { n: formatCount(n, language) });

/** A traffic source in plain words. */
export const sourceName = (channel: Channel, t: TFunction): string => {
  switch (channel) {
    case 'Organic Search':
      return t('Search engines');
    case 'Paid Search':
      return t('Search ads');
    case 'Organic Social':
      return t('Social media');
    case 'Paid Social':
      return t('Social media ads');
    case 'AI':
      return t('AI assistants');
    case 'Referral':
      return t('Other websites');
    case 'Email':
      return t('Email');
    case 'Other Paid':
      return t('Other ads');
    case 'Direct':
      return t('Direct');
    case 'Unknown':
      return t('Unknown');
  }
};

export const hostKindName = (kind: HostKind, t: TFunction): string =>
  kind === 'landing' ? t('Landing pages') : t('Website');

const dateFmt = (language: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  try {
    return new Intl.DateTimeFormat(language || undefined, { ...opts, timeZone: 'UTC' });
  } catch {
    return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: 'UTC' });
  }
};

/**
 * The chart's axis ticks (sparse, so they never crowd) and its per-point tooltip headings (full date).
 * Hourly charts read in UTC, which the first tick and every tooltip say.
 */
export const trendLabels = (
  buckets: number[],
  hourly: boolean,
  language: string,
): { xLabels: string[]; pointLabels: string[] } => {
  if (hourly) {
    const hour = dateFmt(language, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    const dated = dateFmt(language, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    return {
      xLabels: buckets
        .filter((_, i) => i % 4 === 0)
        .map((ms, i) => (i === 0 ? `${hour.format(ms)} UTC` : hour.format(ms))),
      pointLabels: buckets.map((ms) => `${dated.format(ms)} UTC`),
    };
  }
  const day = dateFmt(language, { month: 'short', day: 'numeric' });
  const weekday = dateFmt(language, { weekday: 'short' });
  const full = dateFmt(language, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const step = buckets.length <= 7 ? 1 : buckets.length <= 31 ? 7 : 14;
  return {
    xLabels: buckets.filter((_, i) => i % step === 0).map((ms) => (step === 1 ? weekday.format(ms) : day.format(ms))),
    pointLabels: buckets.map((ms) => full.format(ms)),
  };
};
